import { lazy } from "react";
import { Routes } from "react-router";
import { Outlet, Route } from "react-router-dom";
import { PermissionNames } from "~enums/permissions";
import { AccessDeniedPage, NotFoundPage, ScopeErrorPage } from "~pages/ErrorPages";
import AuthGuard from "../Common/ProtectedRoute";
import OrganizationTabsRoutes from "./OrganizationTabsRoutes";
import SeasonTabsRoutes from "./SeasonTabsRoutes.tsx";

const CompetitionSeasonList = lazy(() => import("../Features/Competitions/CompetitionsSeasonList/CompetitionSeasonList"));
const DivisionsList = lazy(() => import("../Features/Conferences/DivisionsList"));
const Rules = lazy(() => import("../Features/LeadersQualification/Rules"));
const ManageLeague = lazy(() => import("../Features/Leagues/ManageLeague"));
const ManageCompetition = lazy(() => import("../Features/Competitions/ManageCompetition"));
const ManageClub = lazy(() => import("../Features/Club/ManageClub"));
const ManageConferences = lazy(() => import("../Features/Conferences/ManageConferences"));
const ManageDivision = lazy(() => import("../Features/Conferences/ManageDivision"));
const ManageLeadersQualification = lazy(() => import("../Features/LeadersQualification/ManageLeadersQualification"));
const ManageRules = lazy(() => import("../Features/LeadersQualification/ManageRules"));
const ManageMatchProfile = lazy(() => import("../Features/MatchProfiles/ManageMatchProfile"));
const PersonProfile = lazy(() => import("~pages/Persons/PersonProfile/PersonProfile"));
const NewSeason = lazy(() => import("../Features/EditSeason/NewSeason/NewSeason"));
const ManageStandingConfigurations = lazy(() => import("../Features/StandingsConfigurations/ManageStandingConfiguration"));
const ManageVenue = lazy(() => import("../Features/Venues/ManageVenue"));
const ManageSite = lazy(() => import("../Features/Sites/ManageSite"));
const ManageTeam = lazy(() => import("../Features/Team/ManageTeam"));
const PermissionUserDetailsPage = lazy(() => import("~pages/Permissions/PermissionUserDetailsPage/PermissionUserDetailsPage"));
const ManageUserPermissionPage = lazy(() => import("~pages/Permissions/ManageUserPermissionPage/ManageUserPermissionPage"));

const FixtureEditPage = lazy(() => import("../pages/Fixtures/FixtureEditPage"));
const BroadcastPage = lazy(() => import("../pages/Fixtures/BroadcastPage/BroadcastPage.tsx"));
const SeasonRosterPage = lazy(() => import("~pages/Roster/SeasonRosterPage"));
const FixtureRosterPage = lazy(() => import("~pages/Roster/FixtureRosterPage"));
const RosterImages = lazy(() => import("../Features/Roster/RosterImages/RosterImages"));
const Staff = lazy(() => import("../Features/Staff/Staff"));
const EditStandings = lazy(() => import("../Features/Standings/EditStandings/EditStandings"));
const EditStandingsAdjustments = lazy(() => import("../Features/StandingsAdjustments/EditStandingsAdjustments"));
const StandingsAdjustments = lazy(() => import("../Features/StandingsAdjustments/StandingsAdjustments"));
const SeasonEntityEditPage = lazy(() => import("../pages/SeasonEntities/SeasonEntityEditPage"));
const Notifications = lazy(() => import("../pages/Notifications/NotificationsPage.tsx"));
const SelectCustomerPage = lazy(() => import("~pages/StartupWizard/SelectCustomerPage"));
const SelectOrganizationPage = lazy(() => import("~pages/StartupWizard/SelectOrganizationPage"));

const ApplicationRoutes = () => (
  <Routes>
    <Route index element={<SelectCustomerPage />} />
    <Route path="403" element={<AccessDeniedPage />} />
    <Route path=":customerId" element={<Outlet />}>
      <Route path="select/organization" element={<SelectOrganizationPage />} />
      <Route path="notifications" element={<Notifications />} />

      <Route path="admin/:sport/:organizationId/403" element={<AccessDeniedPage />} />
      <Route path="admin/:sport/:organizationId/scope-error" element={<ScopeErrorPage />} />
      {/*ADMIN ROUTES: ORG LEVEL*/}
      <Route
        path="admin/:sport/:organizationId/leagues/manage/:dataId?"
        element={
          <AuthGuard permissionKey={PermissionNames.LEAGUE}>
            <ManageLeague />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/competition/manage/:competitionId?"
        element={
          <AuthGuard permissionKey={PermissionNames.COMPETITION}>
            <ManageCompetition />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/entity-groups/manage/:clubId?"
        element={
          <AuthGuard permissionKey={PermissionNames.ENTITY_GROUP}>
            <ManageClub />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/conferences/manage/:dataId?"
        element={
          <AuthGuard permissionKey={PermissionNames.CONFERENCE}>
            <ManageConferences />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/entities/manage/:entityId?"
        element={
          <AuthGuard permissionKey={PermissionNames.ENTITY}>
            <ManageTeam />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/sites/manage/:dataId?"
        element={
          <AuthGuard permissionKey={PermissionNames.SITE}>
            <ManageSite />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/venues/manage/:dataId?"
        element={
          <AuthGuard permissionKey={PermissionNames.VENUE}>
            <ManageVenue />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/persons/manage/:personId?"
        element={
          <AuthGuard permissionKey={PermissionNames.PERSON}>
            <PersonProfile />
          </AuthGuard>
        }
      />
      <Route
        path={"admin/:sport/:organizationId/standings-configuration/manage/:standingConfigurationId?"}
        element={
          <AuthGuard permissionKey={PermissionNames.STANDING_CONFIGURATION}>
            <ManageStandingConfigurations />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/leaders-qualification/manage"
        element={
          <AuthGuard permissionKey={PermissionNames.LEADER_QUALIFIER}>
            <ManageLeadersQualification />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/match-profiles/manage/:matchProfileId?"
        element={
          <AuthGuard permissionKey={PermissionNames.MATCH_PROFILE}>
            <ManageMatchProfile />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/leaders-qualification/:leaderCriteriaId/qualifiers"
        element={
          <AuthGuard permissionKey={PermissionNames.LEADER_CRITERIA_SET}>
            <Rules />
          </AuthGuard>
        }
      />
      <Route
        path={"admin/:sport/:organizationId/leaders-qualification/:leaderCriteriaId/qualifiers/manage/:dataId?"}
        element={
          <AuthGuard permissionKey={PermissionNames.LEADER_CRITERIA_SET}>
            <ManageRules />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/conferences/:conferenceId/divisions"
        element={
          <AuthGuard permissionKey={PermissionNames.DIVISION}>
            <DivisionsList />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/conferences/:conferenceId/divisions/manage/:divisionId?"
        element={
          <AuthGuard permissionKey={PermissionNames.DIVISION}>
            <ManageDivision />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/permissions/manage/:userId"
        element={
          <AuthGuard permissionKey={PermissionNames.PERMISSIONS}>
            <PermissionUserDetailsPage />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/permissions/manage/:userId/permission/:permissionId?"
        element={
          <AuthGuard permissionKey={PermissionNames.PERMISSIONS}>
            <ManageUserPermissionPage />
          </AuthGuard>
        }
      />
      {/* SEASON LEVEL ROUTES */}

      {/* Season level tabs */}

      <Route
        path="admin/:sport/:organizationId/competition/:competitionId/seasons/"
        element={
          <AuthGuard permissionKey={PermissionNames.COMPETITION}>
            <CompetitionSeasonList />
          </AuthGuard>
        }
      />

      <Route
        path="admin/:sport/:organizationId/competition/:competitionId/seasons/add"
        element={
          <AuthGuard permissionKey={PermissionNames.SEASON}>
            <NewSeason />
          </AuthGuard>
        }
      />

      <Route
        path="admin/:sport/:organizationId/seasons/:seasonId/entities/:entityId"
        element={
          <AuthGuard permissionKey={PermissionNames.SEASON_ENTITY}>
            <SeasonEntityEditPage />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/seasons/:seasonId/entities/:entityId/roster"
        element={
          <AuthGuard permissionKey={PermissionNames.SEASON_ROSTER}>
            <SeasonRosterPage />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/seasons/:seasonId/fixtures/:fixtureId/roster"
        element={
          <AuthGuard permissionKey={PermissionNames.FIXTURE_ROSTER}>
            <FixtureRosterPage />
          </AuthGuard>
        }
      />

      <Route
        path="admin/:sport/:organizationId/seasons/:seasonId/entities/:entityId/roster/images"
        element={
          <AuthGuard permissionKey={PermissionNames.SEASON_ROSTER}>
            <RosterImages />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/seasons/:seasonId/entities/:entityId/staff"
        element={
          <AuthGuard permissionKey={PermissionNames.ROLES}>
            <Staff />
          </AuthGuard>
        }
      />

      <Route
        path="admin/:sport/:organizationId/standings/:standingId"
        element={
          <AuthGuard permissionKey={PermissionNames.STANDING}>
            <EditStandings />
          </AuthGuard>
        }
      />

      <Route
        path="admin/:sport/:organizationId/seasons/:seasonId/standings/adjustments"
        element={
          <AuthGuard permissionKey={PermissionNames.STANDING_ADJUSTMENT}>
            <StandingsAdjustments />
          </AuthGuard>
        }
      />
      <Route
        path={"admin/:sport/:organizationId/seasons/:seasonId/standings/adjustments/:standingsAdjustmentId"}
        element={
          <AuthGuard permissionKey={PermissionNames.STANDING_ADJUSTMENT}>
            <EditStandingsAdjustments />
          </AuthGuard>
        }
      />

      <Route
        path="admin/:sport/:organizationId/seasons/:seasonId/fixtures/:fixtureId"
        element={
          <AuthGuard permissionKey={PermissionNames.FIXTURE}>
            <FixtureEditPage />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/seasons/:seasonId/fixtures/:fixtureId/broadcast"
        element={
          <AuthGuard permissionKey={PermissionNames.FIXTURE}>
            <BroadcastPage />
          </AuthGuard>
        }
      />

      <Route path="admin/:sport/:organizationId/*" element={<OrganizationTabsRoutes />} />
      <Route path="admin/:sport/:organizationId/seasons/:seasonId/*" element={<SeasonTabsRoutes />} />
    </Route>

    <Route path="*" element={<NotFoundPage />} />
  </Routes>
);

export default ApplicationRoutes;
